import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdexiumAd, AdexiumInstance } from '@common/types/adexium';

import { ENV } from '../app/env';
import {
  type AdsCallbackArgsType,
  type AdsPlacementsType,
  type UseAdsHookReturnType,
} from '../types/ads';

type PendingPromise = {
  resolve: () => void;
  reject: (error: Error) => void;
};

const adexiumIdByPlacements: Record<AdsPlacementsType, string> = {
  resource: ENV.ADEXIUM_RESOURCE_BOOST_BLOCK_ID,
  material: ENV.ADEXIUM_MATERIAL_BOOST_BLOCK_ID,
  goods: ENV.ADEXIUM_GOODS_BOOST_BLOCK_ID,
  daily: ENV.ADEXIUM_DAILY_BONUS_BLOCK_ID,
  marketBox: ENV.ADEXIUM_BOX_MARKET_BLOCK_ID,
  welcomeback: ENV.ADEXIUM_WELCOMEBACK_BLOCK_ID,
  chest: ENV.ADEXIUM_CHESTBOX_BLOCK_ID,
  scratch: ENV.ADEXIUM_CHESTBOX_BLOCK_ID,
};

export const useAdexiumAds = (): UseAdsHookReturnType => {
  const [adexium, setAdexium] = useState<AdexiumInstance | null>(null);
  const pendingPromiseRef = useRef<PendingPromise | null>(null);
  const onRewardRef = useRef<() => void | undefined>();

  useEffect(() => {
    if (!adexium) return;

    const handleAdReceived = (ad: AdexiumAd): void => {
      adexium?.displayAd(ad);
    };

    const handleNoAdFound = (): void => {
      if (pendingPromiseRef.current) {
        pendingPromiseRef.current.reject(new Error('Adexium: No ad found'));
        pendingPromiseRef.current = null;
        setAdexium(null);
      }
    };

    const handleAdPlaybackCompleted = (): void => {
      onRewardRef.current?.();

      if (pendingPromiseRef.current) {
        pendingPromiseRef.current.resolve();
        pendingPromiseRef.current = null;
        setAdexium(null);
      }
    };

    adexium?.on('adReceived', handleAdReceived);
    adexium?.on('noAdFound', handleNoAdFound);
    adexium?.on('adPlaybackCompleted', handleAdPlaybackCompleted);

    return (): void => {
      adexium?.off('adReceived', handleAdReceived);
      adexium?.off('noAdFound', handleNoAdFound);
      adexium?.off('adPlaybackCompleted', handleAdPlaybackCompleted);

      if (pendingPromiseRef.current) {
        pendingPromiseRef.current.reject(
          new Error('Adexium widget destroyed before ad finished'),
        );
        pendingPromiseRef.current = null;
      }

      adexium?.destroy?.();
      setAdexium(null);
    };
  }, [adexium]);

  const showAd = useCallback(
    async ({ placements, onReward }: AdsCallbackArgsType): Promise<void> => {
      if (!window.AdexiumWidget) {
        throw new Error('AdexiumWidget script not loaded');
      }

      onRewardRef.current = onReward;

      const widgetId = adexiumIdByPlacements[placements] || '';

      const adexium = new window.AdexiumWidget({
        wid: widgetId,
        adFormat: 'interstitial',
        debug: ENV.IS_DEV,
      });

      setAdexium(adexium);

      return new Promise<void>((resolve, reject) => {
        pendingPromiseRef.current = { resolve, reject };

        try {
          adexium!.requestAd('interstitial');
        } catch (error) {
          pendingPromiseRef.current = null;
          setAdexium(null);
          reject(
            error instanceof Error
              ? error
              : new Error('Failed to run adexium ads'),
          );
        }
      });
    },
    [],
  );

  return showAd;
};
